/**
 * Login Page Component
 *
 * Provides Snowflake credential login with MFA support and local credentials for development.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { snowflakeLogin, localLogin, loading, error: authError } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    passcode: '',
  });
  const [error, setError] = useState('');
  const [showTenants, setShowTenants] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [showMfaHelp, setShowMfaHelp] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear errors when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.username || !formData.password) {
      setError('Please enter username and password');
      return;
    }

    // Get tenant from localStorage or use default
    const tenantId = localStorage.getItem('pulse_tenant_id') || 'R7JD8933';

    // Attempt Snowflake login (role will be determined from user's default)
    const result = await snowflakeLogin(
      formData.username,
      formData.password,
      tenantId,
      formData.passcode || null
    );

    if (result.success) {
      // Redirect to dashboard
      navigate('/');
    } else {
      setError(result.error || 'Login failed. Please check your credentials.');
    }
  };

  const handleUseLocalCredentials = async () => {
    setError('');
    setLoadingTenants(true);

    try {
      const response = await apiClient.get('/api/auth/tenants');
      setTenants(response.data.tenants || []);
      setShowTenants(true);
    } catch (err) {
      setError('Failed to load tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleTenantLogin = async (tenantId) => {
    setError('');

    try {
      const response = await apiClient.post('/api/auth/local-login', {
        tenant_id: tenantId,
      });

      const { token, user, tenant_id } = response.data;

      // Use AuthContext's localLogin to update state
      localLogin(token, user, tenant_id);

      // Redirect to dashboard
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Local login failed');
      console.error('Local login error:', err);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Calon Pulse</h1>
          <p className="login-subtitle">Beta</p>
        </div>

        {!showTenants ? (
          <>
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Snowflake Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter your Snowflake username"
                  disabled={loading}
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Snowflake Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your Snowflake password"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="passcode">
                  Duo Mobile Passcode (Optional)
                  <button
                    type="button"
                    className="help-button"
                    onClick={() => setShowMfaHelp(!showMfaHelp)}
                    aria-label="Show MFA help"
                  >
                    ℹ️
                  </button>
                </label>
                <input
                  type="text"
                  id="passcode"
                  name="passcode"
                  value={formData.passcode}
                  onChange={handleChange}
                  placeholder="Enter 6-digit passcode from Duo Mobile"
                  disabled={loading}
                  autoComplete="off"
                  maxLength="6"
                />
              </div>

              {showMfaHelp && (
                <div className="mfa-help">
                  <h4>Instructions for MFA/Duo Mobile Verification</h4>
                  <ol>
                    <li>Download the Duo Mobile app from your app store.</li>
                    <li>
                      You will receive an enrollment email from Calon. Open the email and
                      click on the enrollment link.
                    </li>
                    <li>Follow the instructions in the email to complete the enrollment process.</li>
                    <li>
                      Once enrolled, you will receive a 6-digit passcode in the Duo Mobile app.
                      Enter this passcode in the field above.
                    </li>
                  </ol>
                  <p className="mfa-help-note">
                    <strong>Note:</strong> The passcode changes every 30 seconds. If login fails,
                    try generating a new passcode.
                  </p>
                  <p className="mfa-help-support">
                    Need help? Contact: <a href="mailto:support@calonanalytics.com">support@calonanalytics.com</a>
                  </p>
                </div>
              )}

              {(error || authError) && (
                <div className="error-message">{error || authError}</div>
              )}

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Authenticating...' : 'Login with Snowflake'}
              </button>
            </form>

            <div className="divider">
              <span>OR</span>
            </div>

            <button
              type="button"
              className="local-credentials-button"
              onClick={handleUseLocalCredentials}
              disabled={loadingTenants}
            >
              {loadingTenants ? 'Loading...' : 'Use Local Credentials (Dev)'}
            </button>
          </>
        ) : (
          <div className="tenant-selector">
            <h3>Select Tenant</h3>
            <p className="tenant-subtitle">Choose which tenant to access</p>

            {error && <div className="error-message">{error}</div>}

            <div className="tenant-list">
              {tenants.map((tenant) => (
                <button
                  key={tenant}
                  className="tenant-button"
                  onClick={() => handleTenantLogin(tenant)}
                >
                  {tenant}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="back-button"
              onClick={() => setShowTenants(false)}
            >
              Back to Login
            </button>
          </div>
        )}

        <div className="login-footer">
          <p className="copyright">© Calon GmbH {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
